// Communication React Query Hooks

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationService } from '../services/api/communicationService';
import {
  Announcement,
  Message,
  FeeReminder,
  FeeDefaulter,
  CommunicationHistory,
  CommunicationStats,
  CreateAnnouncementRequest,
  SendMessageRequest,
  SendFeeReminderRequest,
  FeeReminderFilters,
  CommunicationFilters,
  Conversation,
  ChatMessage,
  ChatMessagesResponse,
  SendChatMessageRequest,
} from '../types/communication';
import toast from 'react-hot-toast';
import { useSignalREvent } from './useCommunicationHub';
import { NOTIFICATION_KEYS } from './useNotifications';
import { useAuthStore } from '../store/authstore';
import { UnreadCountResponse } from '../types/communication';

interface NewMessagePayload {
  messageId: number;
  conversationId: number;
  senderId: string;
  senderName: string;
  content: string;
  sentAt: string;
  isRead: boolean;
}

// Query keys for cache management
export const COMMUNICATION_KEYS = {
  all: ['communication'] as const,
  announcements: () => [...COMMUNICATION_KEYS.all, 'announcements'] as const,
  announcement: (id: string) => [...COMMUNICATION_KEYS.all, 'announcement', id] as const,
  messages: () => [...COMMUNICATION_KEYS.all, 'messages'] as const,
  message: (id: string) => [...COMMUNICATION_KEYS.all, 'message', id] as const,
  feeDefaulters: (filters?: FeeReminderFilters) =>
    [...COMMUNICATION_KEYS.all, 'fee-defaulters', filters] as const,
  feeReminderHistory: () => [...COMMUNICATION_KEYS.all, 'fee-reminder-history'] as const,
  stats: () => [...COMMUNICATION_KEYS.all, 'stats'] as const,
  history: (filters?: CommunicationFilters) =>
    [...COMMUNICATION_KEYS.all, 'history', filters] as const,
  conversations: () => [...COMMUNICATION_KEYS.all, 'conversations'] as const,
  conversationMessages: (id: string) =>
    [...COMMUNICATION_KEYS.all, 'conv-messages', id] as const,
};

// ============================================
// ANNOUNCEMENT HOOKS
// ============================================

export const useAnnouncements = (filters?: CommunicationFilters) => {
  return useQuery({
    queryKey: COMMUNICATION_KEYS.announcements(),
    queryFn: () => communicationService.getAnnouncements(filters),
  });
};

export const useAnnouncement = (id: string) => {
  return useQuery({
    queryKey: COMMUNICATION_KEYS.announcement(id),
    queryFn: () => communicationService.getAnnouncementById(id),
    enabled: Boolean(id),
  });
};

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => 
      communicationService.createAnnouncement(data),
    onSuccess: (newAnnouncement) => {
      toast.success(
        newAnnouncement.messages[0]
      );
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.announcements() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.stats() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.history() 
      });
    },
    onError: (error: Error) => {
      console.error('Error creating announcement:', error);
    },
  });
};

// ============================================
// MESSAGING HOOKS
// ============================================

export const useMessages = (filters?: CommunicationFilters) => {
  return useQuery({
    queryKey: COMMUNICATION_KEYS.messages(),
    queryFn: () => communicationService.getMessages(filters),
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessageRequest) => 
      communicationService.sendMessage(data),
    onSuccess: (newMessage) => {
      toast.success(
        `Message sent to ${newMessage.recipients.length} recipient(s)!`
      );
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.messages() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.stats() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.history() 
      });
    },
    onError: (error: Error) => {
      console.error('Error sending message:', error);
    },
  });
};

export const useMarkMessageAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => 
      communicationService.markMessageAsRead(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.messages() 
      });
    },
  });
};

// ============================================
// FEE REMINDER HOOKS
// ============================================

export const useStudentsWithDues = (filters?: FeeReminderFilters) => {
  return useQuery<FeeDefaulter[]>({
    queryKey: COMMUNICATION_KEYS.feeDefaulters(filters),
    queryFn: () => communicationService.getStudentsWithDues(filters),
  });
};

export const useSendFeeReminders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendFeeReminderRequest) => 
      communicationService.sendFeeReminders(data),
    onSuccess: (result) => {
      toast.success(
        `Fee reminders sent to ${result.studentIds.length} student(s)!`
      );
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.feeDefaulters() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.feeReminderHistory() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.stats() 
      });
      queryClient.invalidateQueries({ 
        queryKey: COMMUNICATION_KEYS.history() 
      });
    },
    onError: (error: Error) => {
      console.error('Error sending fee reminders:', error);
    },
  });
};

export const useFeeReminderHistory = () => {
  return useQuery({
    queryKey: COMMUNICATION_KEYS.feeReminderHistory(),
    queryFn: () => communicationService.getFeeReminderHistory(),
  });
};

// ============================================
// STATS & HISTORY HOOKS
// ============================================

export const useCommunicationStats = () => {
  return useQuery<CommunicationStats>({
    queryKey: COMMUNICATION_KEYS.stats(),
    queryFn: () => communicationService.getCommunicationStats(),
  });
};

export const useCommunicationHistory = (filters?: CommunicationFilters) => {
  return useQuery<CommunicationHistory[]>({
    queryKey: COMMUNICATION_KEYS.history(filters),
    queryFn: () => communicationService.getCommunicationHistory(filters),
  });
};

// ============================================
// DIRECT MESSAGING HOOKS
// ============================================

export const useConversations = () => {
  return useQuery<Conversation[]>({
    queryKey: COMMUNICATION_KEYS.conversations(),
    queryFn: () => communicationService.getConversations(),
  });
};

export const useConversationMessages = (conversationId: string | number | null, lastMessageId?: number) => {
  const idStr = conversationId != null ? String(conversationId) : '';
  return useQuery<ChatMessagesResponse>({
    queryKey: COMMUNICATION_KEYS.conversationMessages(idStr),
    queryFn: () => communicationService.getConversationMessages(idStr, lastMessageId),
    enabled: Boolean(idStr),
    //staleTime: 30_000,
  });
};

export const useSendChatMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      communicationService.sendChatMessage(conversationId, { content }),
    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_KEYS.conversationMessages(conversationId),
      });
      queryClient.invalidateQueries({ queryKey: COMMUNICATION_KEYS.conversations() });
    },
    onError: () => {
    },
  });
};

export const useStartConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      recipientId,
      name,
      role,
      className,
      department,
    }: {
      recipientId: string;
      name: string;
      role: 'STUDENT' | 'EMPLOYEE';
      className?: string;
      department?: string;
    }) =>
      communicationService.startConversationWithDetails(
        recipientId,
        name,
        role,
        className,
        department
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATION_KEYS.conversations() });
    },
    onError: () => {
    },
  });
};

export const useMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      communicationService.markConversationRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMUNICATION_KEYS.conversations() });
    },
  });
};

export const useNotificationSignalR = () => {
  const queryClient = useQueryClient();

  useSignalREvent<Record<string, number>>('OnUnreadCountUpdate', (counts) => {
    queryClient.setQueryData<UnreadCountResponse>(NOTIFICATION_KEYS.unreadCount(), {
      unreadNotifications: counts['notifications'] ?? 0,
      unreadTotalMessages: counts['messages'] ?? 0,
      unreadConversations: counts['conversations'] ?? 0,
      lastCheckedAt: new Date().toISOString(),
    });
  });

  useSignalREvent('OnNewNotification', () => {
    queryClient.invalidateQueries({ queryKey: [...NOTIFICATION_KEYS.all, 'list'] });
  });
};

export const useConversationSignalR = (activeConversationId: string | number | null) => {
  const queryClient = useQueryClient();
  const { session } = useAuthStore();
  const currentUserId = session?.user.id;

  useSignalREvent<NewMessagePayload>('OnNewMessage', (payload) => {
    console.log('[OnNewMessage] payload:', payload) ;
    const { messageId, conversationId, senderId, senderName, content, sentAt, isRead } = payload;
    const convIdStr = String(conversationId);
    const activeIdStr = activeConversationId != null ? String(activeConversationId) : null;

    const newMsg: ChatMessage = {
      id: String(messageId),
      conversationId: convIdStr,
      content,
      sentBy: { id: senderId, name: senderName },
      sentAt,
      isRead,
      isMine: senderId === currentUserId,
    };

    // Append to messages cache for this conversation (deduplicate by id)
    queryClient.setQueryData<ChatMessagesResponse>(
      COMMUNICATION_KEYS.conversationMessages(convIdStr),
      (old) => {
        const existing = old?.messages ?? [];
        if (existing.some(m => m.id === String(messageId))) return old!;
        return {
          messages: [...existing, newMsg],
          totalMessages: (old?.totalMessages ?? 0) + 1,
          isLast: old?.isLast ?? true,
        };
      }
    );

    // Update conversation list: last message preview + unread count
    queryClient.setQueryData<Conversation[]>(
      COMMUNICATION_KEYS.conversations(),
      (old) =>
        old?.map((conv) =>
          conv.id === convIdStr
            ? {
                ...conv,
                lastMessage: { content, sentAt, sentByMe: senderId === currentUserId },
                updatedAt: sentAt,
                unreadCount:
                  conv.id === activeIdStr || senderId === currentUserId
                    ? conv.unreadCount
                    : conv.unreadCount + 1,
              }
            : conv
        ) ?? []
    );
  });
};

export const usePeopleSearch = (query: string) => {
  return useQuery({
    queryKey: [...COMMUNICATION_KEYS.all, 'people-search', query],
    queryFn: () => communicationService.searchPeople(query),
  });
};
